import type { ReactNode } from "react";
/* eslint-disable import/order */
import { AutoScrollSlider } from "@/features/shop/components/AutoScrollSlider/AutoScrollSlider";
import { CarouselSlider } from "@/features/shop/components/Carousel";
import { SearchBarAutocompleteSection } from "@/features/shop/components/SearchBarAutocomplete";
import { CmsPromotionCard } from "@/features/shop/cms/components/CmsPromotionCard";
import { cmsBlockKey, transformCmsBlocksToAutoScrollCards, transformCmsBlocksToSlides } from "@/features/shop/cms/utils/cmsBlockTransformers";
import { SHOP_PAGE_CONFIG } from "@/features/shop/config/ShopPageConfig";
import type { CmsZone } from "@/features/shop/cms/types/cms.types";

const GRID_COLUMNS = 6;

const COLUMN_SPAN_CLASSES: Record<number, string> = {
  1: "col-span-1 sm:col-span-2 lg:col-span-1",
  2: "col-span-1 sm:col-span-2 lg:col-span-2",
  3: "col-span-1 sm:col-span-2 lg:col-span-3",
  4: "col-span-1 sm:col-span-2 lg:col-span-4",
  5: "col-span-1 sm:col-span-2 lg:col-span-5",
  6: "col-span-1 sm:col-span-2 lg:col-span-6",
};

const spanClassCache = new Map<string, string>();

const parseZoneSpan = (zoneCode: string): number => {
  const match = zoneCode.match(/[-_](\d+)[/-](\d+)/);
  if (!match) {
    return GRID_COLUMNS;
  }

  const [, span, total] = match;
  const parsedSpan = Number(span);
  const parsedTotal = Number(total);

  if (!Number.isFinite(parsedSpan) || parsedTotal !== GRID_COLUMNS) {
    return GRID_COLUMNS;
  }

  return Math.min(Math.max(parsedSpan, 1), GRID_COLUMNS);
};

const getColumnSpanClass = (zoneCode: string): string => {
  const cacheKey = zoneCode.toLowerCase();
  const cached = spanClassCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const span = parseZoneSpan(zoneCode);
  const className = COLUMN_SPAN_CLASSES[span] ?? COLUMN_SPAN_CLASSES[GRID_COLUMNS];
  spanClassCache.set(cacheKey, className);
  return className;
};

const parseContentTypeSpan = (contentType?: string | null): string | undefined => {
  if (!contentType) {
    return undefined;
  }

  const match = contentType.toLowerCase().match(/(\d+)[/-](\d+)/);
  if (!match) {
    return undefined;
  }

  const span = Number(match[1]);
  const total = Number(match[2]);

  if (!Number.isFinite(span) || total !== GRID_COLUMNS) {
    return undefined;
  }

  return COLUMN_SPAN_CLASSES[Math.min(Math.max(span, 1), GRID_COLUMNS)];
};

type ZoneKind = "slider" | "promotion" | "infinite" | "search";

type ZoneRenderer = {
  kind: ZoneKind
  matches: (zone: CmsZone) => boolean
  render: (zone: CmsZone, autoplayInterval: number, pageCode: string) => ZoneRenderItem[] | null
};

type ZoneRenderItem = {
  key: string
  spanClass: string
  content: ReactNode
};

const ZONE_RENDERERS: ZoneRenderer[] = [
  {
    kind: "slider",
    matches: (zone) => zone.carrousel && zone.code.toLowerCase().startsWith("slider"),
    render: (zone, autoplayInterval, _pageCode) => {
      if (!zone.blocks.length) {
        return [];
      }

      const slides = transformCmsBlocksToSlides(zone.blocks);

      if (slides.length === 0) {
        return [];
      }

      return [
        {
          key: zone.code,
          spanClass: getColumnSpanClass(zone.code),
          content: (
            <CarouselSlider
              slides={slides}
              autoplayInterval={autoplayInterval}
              className="h-[240px] sm:h-[300px] md:h-[360px] lg:h-[400px]"
            />
          ),
        },
      ];
    },
  },
  {
    kind: "promotion",
    matches: (zone) => !zone.carrousel && zone.code.toLowerCase().startsWith("promotional-card"),
    render: (zone, _autoplayInterval, pageCode) => {
      if (!zone.blocks.length) {
        return [];
      }

      const sortedBlocks = [...zone.blocks].sort((a, b) => a.position - b.position);
      const zoneSpanClass = getColumnSpanClass(zone.code);

      return sortedBlocks.map((block, index) => ({
        key: `${zone.code}-${cmsBlockKey(block, index)}`,
        spanClass: parseContentTypeSpan(block.content_type) ?? zoneSpanClass,
        content: (
          <CmsPromotionCard
            pageCode={pageCode}
            zoneCode={zone.code}
            blockIndex={index}
            containerClassName="h-full min-h-[160px] sm:min-h-[220px] md:min-h-[280px] lg:min-h-[340px]"
          />
        ),
      }));
    },
  },
  {
    kind: "infinite",
    matches: (zone) => zone.code.toLowerCase().startsWith("infinit"),
    render: (zone, _autoplayInterval, _pageCode) => {
      if (!zone.blocks.length) {
        return [];
      }

      const cards = transformCmsBlocksToAutoScrollCards(zone.blocks);
      if (cards.length === 0) {
        return [];
      }

      return [
        {
          key: zone.code,
          spanClass: getColumnSpanClass(zone.code),
          content: (
            <AutoScrollSlider
              cards={cards}
              scrollSpeed={SHOP_PAGE_CONFIG.AUTO_SCROLL_SLIDER_SPEED}
            />
          ),
        },
      ];
    },
  },
  {
    kind: "search",
    matches: (zone) => zone.code.toLowerCase().startsWith("search"),
    render: (zone, _autoplayInterval, _pageCode) => {
      if (!zone.blocks.length) {
        return [];
      }

      const sortedBlocks = [...zone.blocks].sort((a, b) => a.position - b.position);
      const zoneSpanClass = getColumnSpanClass(zone.code);

      return sortedBlocks.map((block, index) => ({
        key: `${zone.code}-${cmsBlockKey(block, index)}`,
        spanClass: parseContentTypeSpan(block.content_type) ?? zoneSpanClass,
        content: (
          <SearchBarAutocompleteSection
            title={block.title ?? undefined}
            placeholder={block.subtitle ?? undefined}
          />
        ),
      }));
    },
  },
];

type CmsZoneLayoutProps = {
  pageCode: string
  zones: CmsZone[]
  autoplayInterval?: number
  kinds?: ZoneKind[]
};

export const CmsZoneLayout = ({
  pageCode,
  zones,
  autoplayInterval = SHOP_PAGE_CONFIG.BANNER_SLIDER_AUTOPLAY_INTERVAL,
  kinds = ["slider"],
}: CmsZoneLayoutProps) => {
  const renderQueue = zones.reduce<ZoneRenderItem[]>((items, zone) => {
    const renderer = ZONE_RENDERERS.find((candidate) => candidate.matches(zone) && kinds.includes(candidate.kind));
    if (!renderer) {
      return items;
    }

    const renderedItems = renderer.render(zone, autoplayInterval, pageCode);
    if (!renderedItems || renderedItems.length === 0) {
      return items;
    }

    return [...items, ...renderedItems.map((item) => ({
      ...item,
      key: `${pageCode}-${item.key}`,
    }))];
  }, []);

  if (renderQueue.length === 0) {
    return (
      <div className="w-full rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
        Aucune zone CMS valide détectée pour ce type.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-6 w-full">
      {renderQueue.map(({ key, spanClass, content }) => (
        <div
          key={key}
          className={spanClass}
        >
          {content}
        </div>
      ))}
    </div>
  );
};
